/**
 * K5 — migrate an account's existing plaintext logins to ciphertext
 * (docs/DESIGN_K5_credential-encryption.md §5). Runs on the owner's device
 * whenever the master key becomes available (unlock, setting the code, an
 * app start with the key cached), never on the server:
 *
 *   1. status: already migrated → nothing to do (one cheap GET);
 *   2. inventory: every login record, whole;
 *   3. for each record with a plaintext secret field: encrypt those fields,
 *      write the record back (the server destroys the earlier versions);
 *   4. when every record went through: ask the server to mark the account
 *      migrated (it verifies first). From then on plaintext is refused.
 *
 * Idempotent (the `fkc1:` prefix is the state), single-flight, and never
 * throws to its callers: a failure is logged and the next run retries.
 */
import { CREDENTIAL_WRITES_ENCRYPTED } from '../../config/credentialEncryption';
import { vaultService } from '../vaultService';
import { createLogger } from '../../utils/logger';
import { deriveCredentialKey, encryptField, isCredentialCiphertext } from './CredentialCipher';
import { getCachedMasterKey, isWrappedMasterKeyEnrolled, onMasterKeyCached } from './MasterKeyService';

const logger = createLogger('credentialMigration');

export type CredentialMigrationStatus =
  /** Switch off, no Recovery Code, or already migrated. */
  | 'skipped'
  /** Enrolled, but the key is not on this device yet. */
  | 'locked'
  /** Every record is ciphertext and the server marked the account. */
  | 'done'
  /** Some rewrites failed, or the server still found plaintext; retried next run. */
  | 'partial'
  | 'error';

export interface CredentialMigrationResult {
  status: CredentialMigrationStatus;
  records: number;
  rewritten: number;
  failed: number;
}

let inFlight: Promise<CredentialMigrationResult> | null = null;

async function run(): Promise<CredentialMigrationResult> {
  const none = (status: CredentialMigrationStatus): CredentialMigrationResult => ({
    status,
    records: 0,
    rewritten: 0,
    failed: 0,
  });
  if (!CREDENTIAL_WRITES_ENCRYPTED) return none('skipped');

  const { credentialsEncryptedAt } = await vaultService.getCredentialMigrationStatus();
  if (credentialsEncryptedAt) return none('skipped');
  if (!(await isWrappedMasterKeyEnrolled())) return none('skipped');
  const masterKey = await getCachedMasterKey();
  if (!masterKey) return none('locked');
  const key = await deriveCredentialKey(masterKey);

  const { records } = await vaultService.getCredentialInventory();
  let rewritten = 0;
  let failed = 0;
  for (const record of records) {
    const pending = record.secretFields.filter((f) => {
      const v = record.data?.[f];
      return typeof v === 'string' && v !== '' && !isCredentialCiphertext(v);
    });
    if (pending.length === 0) continue;
    try {
      const data: Record<string, unknown> = { ...record.data };
      for (const f of pending) data[f] = await encryptField(record.data[f] as string, key);
      await vaultService.rewriteCredentialSecret(record.path, data);
      rewritten += 1;
    } catch (err) {
      failed += 1;
      logger.warn(`Could not rewrite ${record.kind} record ${record.id}: ${(err as Error)?.message}`);
    }
  }

  if (failed > 0) {
    logger.warn(`Credential migration partial: ${rewritten} rewritten, ${failed} failed of ${records.length}`);
    return { status: 'partial', records: records.length, rewritten, failed };
  }
  const marked = await vaultService.markCredentialsMigrated();
  if (!marked.credentialsEncryptedAt) {
    logger.warn(`Server still sees ${marked.plaintextFields} plaintext login fields; will retry`);
    return { status: 'partial', records: records.length, rewritten, failed };
  }
  logger.info(`Saved logins migrated: ${rewritten} of ${records.length} records rewritten`);
  return { status: 'done', records: records.length, rewritten, failed };
}

/**
 * Run the migration if this account still needs it. Safe to call often;
 * concurrent callers share one run.
 */
export function runCredentialMigrationIfNeeded(): Promise<CredentialMigrationResult> {
  if (inFlight) return inFlight;
  inFlight = run()
    .catch((err): CredentialMigrationResult => {
      logger.warn(`Credential migration did not complete: ${(err as Error)?.message}`);
      return { status: 'error', records: 0, rewritten: 0, failed: 0 };
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

// Whenever the master key lands on this device (unlock, enrollment), give
// the migration a chance. Registered once, at module load.
onMasterKeyCached(() => {
  void runCredentialMigrationIfNeeded();
});

/** Test hook. */
export const __internal = { run };
